import React from 'react';
import { motion } from 'framer-motion';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  HomeIcon,
  CpuChipIcon,
  Cog6ToothIcon,
  ChatBubbleLeftRightIcon,
  PuzzlePieceIcon,
  EyeIcon,
  SparklesIcon,
  RocketLaunchIcon,
  BuildingOfficeIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';
import { 
  HomeIcon as HomeIconSolid,
  CpuChipIcon as CpuChipIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid,
  PuzzlePieceIcon as PuzzlePieceIconSolid,
  EyeIcon as EyeIconSolid,
  BuildingOfficeIcon as BuildingOfficeIconSolid,
  BeakerIcon as BeakerIconSolid
} from '@heroicons/react/24/solid';

import { agentsService } from '@/services/agents';
import { workflowsService } from '@/services/workflows';
import { workspacesService } from '@/services/workspaces';
import { integrationsService } from '@/services/integrations';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/contexts/AuthContext';
import type { Agent, Workflow, Workspace, Integration } from '@/types';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  iconSolid: React.ComponentType<any>;
  badge?: string | number;
  gradient: string;
}

interface NavigationSection {
  title: string;
  items: NavigationItem[];
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { currentWorkspace } = useWorkspace();
  const { isAuthenticated } = useAuth();

  // Get workspace ID
  const workspaceId = currentWorkspace?.id;

  // Fetch real data counts - only when authenticated and workspace is available
  const { data: agentsData } = useQuery({
    queryKey: ['agents', workspaceId],
    queryFn: () => agentsService.getAgents({
      workspace_id: workspaceId!,
      page: 1,
      per_page: 100 // Get all for counting
    }),
    enabled: isAuthenticated && !!workspaceId,
    staleTime: 30000 // Cache for 30 seconds
  });

  const { data: workflowsData } = useQuery({
    queryKey: ['workflows', workspaceId],
    queryFn: () => workflowsService.getWorkflows(workspaceId!, 1, 100),
    enabled: isAuthenticated && !!workspaceId,
    staleTime: 30000
  });

  const { data: workspacesData } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => workspacesService.getWorkspaces({ page: 1, per_page: 100 }),
    enabled: isAuthenticated,
    staleTime: 30000
  });

  const { data: integrationsData } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => integrationsService.getIntegrations(1, 100),
    enabled: isAuthenticated,
    staleTime: 30000
  });

  // Count active items
  const agentCount = agentsData?.agents?.filter((agent: Agent) => agent.is_active).length || 0;
  const workflowCount = workflowsData?.items?.filter((workflow: Workflow) => workflow.is_active).length || 0;
  const workspaceCount = workspacesData?.items?.filter((workspace: Workspace) => workspace.is_active).length || 0;
  const integrationCount = integrationsData?.items?.filter((integration: Integration) => integration.status === 'connected').length || 0;

  const navigationSections: NavigationSection[] = [
    {
      title: 'Core',
      items: [
        {
          name: 'Dashboard',
          href: '/dashboard',
          icon: HomeIcon,
          iconSolid: HomeIconSolid,
          gradient: 'from-indigo-500 to-purple-600'
        },
        {
          name: 'Workflow Assistant',
          href: '/chat',
          icon: ChatBubbleLeftRightIcon,
          iconSolid: ChatBubbleLeftRightIconSolid,
          gradient: 'from-emerald-500 to-teal-600'
        }
      ]
    },
    {
      title: 'Management',
      items: [
        {
          name: 'Agents',
          href: '/agents',
          icon: CpuChipIcon,
          iconSolid: CpuChipIconSolid,
          badge: agentCount > 0 ? agentCount.toString() : undefined,
          gradient: 'from-blue-500 to-cyan-600'
        },
        {
          name: 'Workflows',
          href: '/workflows',
          icon: Cog6ToothIcon,
          iconSolid: Cog6ToothIconSolid,
          badge: workflowCount > 0 ? workflowCount.toString() : undefined,
          gradient: 'from-purple-500 to-pink-600'
        },
        {
          name: 'Workspaces',
          href: '/workspaces',
          icon: BuildingOfficeIcon,
          iconSolid: BuildingOfficeIconSolid,
          badge: workspaceCount > 0 ? workspaceCount.toString() : undefined,
          gradient: 'from-teal-500 to-green-600'
        },
        {
          name: 'Integrations',
          href: '/integrations',
          icon: PuzzlePieceIcon,
          iconSolid: PuzzlePieceIconSolid,
          badge: integrationCount > 0 ? integrationCount.toString() : undefined,
          gradient: 'from-amber-500 to-orange-600'
        }
      ]
    },
    {
      title: 'Analytics',
      items: [
        {
          name: 'Monitoring',
          href: '/monitoring',
          icon: EyeIcon,
          iconSolid: EyeIconSolid,
          gradient: 'from-rose-500 to-red-600'
        }
      ]
    }
  ];

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  const Logo = () => (
    <div className="flex items-center space-x-3 p-4 border-b border-gray-200 dark:border-gray-700">
      <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-600 rounded-lg shadow-sm flex items-center justify-center">
        <SparklesIcon className="h-5 w-5 text-white" />
      </div>
      <div className="flex flex-col">
        <h1 className="text-lg font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          Orchestra
        </h1>
        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">AI Orchestration</p>
      </div>
    </div>
  );

  const NavigationItem: React.FC<{ item: NavigationItem }> = ({ item }) => {
    const active = isActive(item.href);
    const Icon = active ? item.iconSolid : item.icon;

    return (
      <NavLink
        to={item.href}
        className="block"
      >
        <div className={`group relative mx-2 mb-1 rounded-lg transition-all duration-200 overflow-hidden ${
          active ? 'shadow-sm' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
        }`}>
          {/* Active background with gradient - positioned behind content */}
          {active && (
            <div 
              className={`absolute inset-0 bg-gradient-to-r ${item.gradient} opacity-100`}
            />
          )}
          
          <div className="flex items-center px-3 py-2.5 relative">
            <div className={`flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 ${
              active 
                ? 'bg-white/20 dark:bg-white/10' 
                : 'bg-transparent group-hover:bg-gray-100 dark:group-hover:bg-gray-600/50'
            }`}>
              <Icon className={`h-5 w-5 transition-colors duration-200 ${
                active 
                  ? 'text-white' 
                  : 'text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-200'
              }`} />
            </div>
            
            <div className="flex-1 ml-3 flex items-center justify-between">
              <span className={`font-medium text-sm transition-colors duration-200 ${
                active 
                  ? 'text-white' 
                  : 'text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100'
              }`}>
                {item.name}
              </span>
              {item.badge && (
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full transition-colors duration-200 ${
                  active
                    ? 'bg-white/20 dark:bg-white/10 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 group-hover:bg-gray-300 dark:group-hover:bg-gray-600'
                }`}>
                  {item.badge}
                </span>
              )}
            </div>
          </div>
        </div>
      </NavLink>
    );
  };

  return (
    <aside className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-sm flex flex-col h-full transition-colors duration-200">
      {/* Logo */}
      <Logo />

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="space-y-6">
          {navigationSections.map((section) => (
            <div key={section.title}>
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-4">
                {section.title}
              </h3>
              <div className="space-y-1">
                {section.items.map((item) => (
                  <NavigationItem key={item.name} item={item} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </nav>

      {/* Bottom Action */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2.5 rounded-lg font-medium text-sm shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center space-x-2"
        >
          <RocketLaunchIcon className="h-4 w-4" />
          <span>Quick Deploy</span>
        </motion.button>
        
        {/* Status Indicator */}
        <div className="flex items-center justify-center mt-3 text-xs text-gray-500 dark:text-gray-400">
          <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2" />
          <span>All systems operational</span>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar; 